import * as React from 'react';
import type { DateRangePreset } from '../DatePicker.types';
import { DateRangeFixedPresets } from './DateRangeFixedPresets';
import Calendar from '../../Calendar/Calendar';
import { DateRangePicker } from './DateRangePicker';
import { ACTION, ACTION_ID, createDateString, getDateRange } from '../utils';
import LabelButton from '../../button/LabelButton/LabelButton';
import { cn } from '../../../utils';

interface Props {
  range: boolean;
  calendarProps:
    | [React.ComponentProps<typeof Calendar>, React.ComponentProps<typeof Calendar>?]
    | React.ComponentProps<typeof Calendar>;
  showFooter: boolean;
  fixedDatesShortcut?: boolean;
  fixedDatesList?: DateRangePreset[];
  dateValue: Date | [Date, Date] | null;
  onChange: (date: Date | [Date, Date]) => void;
  onCaptureAction?: (...args) => void;
  setOpenPopover: (open: boolean) => void;
  setDateValue: (date: Date | [Date, Date]) => void;
  setDateSelected: (val: 'start' | 'end' | undefined) => void;
}

export function DatePickerPopoverContent({
  range,
  calendarProps,
  showFooter,
  fixedDatesShortcut,
  fixedDatesList,
  dateValue,
  onChange,
  onCaptureAction,
  setOpenPopover,
  setDateValue,
  setDateSelected,
}: Props) {
  const rangeStart = dateValue?.[0];
  const rangeEnd = dateValue?.[1];

  function onApplyChanges(date?: Date | [Date, Date]) {
    setOpenPopover(false);
    if (onChange) {
      const newValue = date || (dateValue as any);
      if (range && !newValue[0] && newValue[1]) {
        return;
      }
      onChange(newValue);
      onCaptureAction?.(ACTION.CLICK, ACTION_ID.CUSTOM_RANGE, dateValue as any);
    }
  }

  function onSelectFixedDate(
    date: Exclude<ACTION_ID, ACTION_ID.CUSTOM_RANGE | ACTION_ID.SINGLE>
  ) {
    if (!range) return;
    setOpenPopover(false);
    const valueToSet = getDateRange(date);
    showFooter ? setDateValue(valueToSet) : onApplyChanges(valueToSet);
    onCaptureAction?.(ACTION.CLICK, date, valueToSet);
  }

  function onSelectCustomPresetDate(label: string) {
    const datePreset = fixedDatesList?.find((d) => d.label === label);
    if (datePreset) {
      const dateRange: [Date, Date] = [
        new Date(datePreset.startDate),
        new Date(datePreset.endDate),
      ];
      range && (showFooter ? setDateValue(dateRange) : onApplyChanges(dateRange));
      range && onCaptureAction?.(ACTION.CLICK, ACTION_ID.CUSTOM_RANGE, dateRange);
    }
  }
  return (
    <div className='sui-flex-col sui-flex'>
      <div className='sui-flex sui-flex-row'>
        {fixedDatesShortcut && (
          <DateRangeFixedPresets
            customPresets={fixedDatesList}
            onSelectFixedDate={onSelectFixedDate}
            onSelectCustomDate={onSelectCustomPresetDate}
          />
        )}
        <div className='sui-flex sui-flex-col sui-gap-2 sui-p-1'>
          {!range && (
            <Calendar
              selected={dateValue as Date}
              onChange={(date) => {
                setDateValue?.(date);
                (
                  onCaptureAction as (
                    action: ACTION,
                    name: ACTION_ID,
                    date?: Date
                  ) => void
                )?.(ACTION.CLICK, ACTION_ID.SINGLE, date);
                !showFooter && onApplyChanges(date);
              }}
              {...calendarProps}
            />
          )}
          {range && (
            <DateRangePicker
              startDate={rangeStart}
              endDate={rangeEnd}
              setDateSelected={setDateSelected}
              calendarProps={
                calendarProps as [
                  React.ComponentProps<typeof Calendar>,
                  React.ComponentProps<typeof Calendar>,
                ]
              }
              onChange={(dates) => {
                setDateValue(dates);
                onCaptureAction?.(ACTION.CLICK, ACTION_ID.CUSTOM_RANGE, dates);
                !showFooter && dates[0] && dates[1] && onApplyChanges(dates);
              }}
            />
          )}
          {showFooter && (
            <div className='sui-border-t sui-border-solid sui-border-gray-90 sui-w-full sui-justify-between sui-flex sui-pt-1 sui-flex-row'>
              <span
                className={cn(
                  'sui-text-gray-40/100 sui-text-desktop-3 sui-content-center',
                  !range && 'sui-invisible'
                )}
              >
                {`Range: ${rangeStart ? createDateString(rangeStart) : '--'} - ${rangeEnd ? createDateString(rangeEnd) : '--'}`}
              </span>
              <div className='sui-flex sui-gap-1'>
                <LabelButton
                  variantType='secondary'
                  onClick={() => {
                    setOpenPopover(false);
                    onCaptureAction?.(ACTION.CLICK, ACTION_ID.CANCEL);
                  }}
                >
                  Cancel
                </LabelButton>
                <LabelButton variantType='primary' onClick={() => onApplyChanges()}>
                  Apply
                </LabelButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
